import { useEffect, useState } from "react";
import {
  fetchAllPools,
  fetchPoolById,
} from "../services/subraphs/vaultSubgraph";
import {
  coingeckoDefaultIds,
  countriesDetails,
  currenciesToFiat,
  PoolType,
  securityCategoriesAlias,
  tokenGcexAlias,
} from "../utils/constants";
import {
  fetchSecurityByAddress,
  fetchUserMarginProfitAndLoss,
} from "../services/subraphs/walletSubraph";
import { ethers } from "ethers";
import { getDefaultChainWeb3 } from "../services/contracts";
import SECURITYABI from "../utils/abis/Security.json";
import SECONDARYMANAGERABI from "../utils/abis/secondaryManager.json";
import SECURITYFACTORYABI from "../utils/abis/SecurityFactory.json";
import {
  convertBytes32ToIpfsHash,
  readIpfsDocumentFromHash,
} from "../lib/pinataIpfs";
import { contractAddress } from "@verified-network/verified-sdk";
import useNetworkStore from "../store/networkStore";
import {
  capitalizeFirstLetter,
  convertFromBlockTimestamp,
  fetchTokenPriceFromVerified,
  formatNumberWithUnits,
  getYesterdayTimeFromNow,
} from "../utils/helpers/utils";
import {
  fetchTickerDetails,
  getTickerCoporateActions,
} from "../utils/helpers/gtn";

export const useGetAllPools = (
  updateList,
  investorAddress = null,
  userCountry = null,
  userType = null,
  verifiedWalletDetails,
  custodyWalletDetails,
  includeResolutions = false,
  allTokensIdCoinecko,
  poolId = null,
  newChainId = null,
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedBlock, setFetchedBlock] = useState(null);
  const { chainId, provider, web3 } = useNetworkStore();

  useEffect(() => {
    let shouldPopulate = true;
    const fetchAndFormatAllPools = async () => {
      setLoading(true);
      setError(null);
      setData([]); //clear data on refresh?
      try {
        let _web3;
        if (_web3) {
          _web3 = web3;
        } else {
          _web3 = getDefaultChainWeb3();
        }
        //step1: fetch all pools
        const allPoolsRaw = !poolId
          ? await fetchAllPools(chainId).then((res) => res)
          : newChainId
            ? await fetchPoolById(Number(newChainId), poolId)
            : [];

        // console.log("allPoolsRaw======", allPoolsRaw);

        const blockNumber = await _web3.eth.getBlockNumber();

        setFetchedBlock(Number(blockNumber));

        let fetchedPNL = [];
        let marginFetchedPrices = {};
        async function formatPoolsSequentially() {
          const formattedPoolsArray = [];
          for (let i = 0; i < allPoolsRaw.length; i++) {
            const pool = allPoolsRaw[i];
            let volumeChartData = [];
            let volumeCandleStickData = [];
            let pricesRaw = [];
            let isSecurityQualified = false;
            let securityType,
              prices,
              priceChartData = [],
              priceCandleStickData = [],
              currentPrice,
              margin,
              minOrderSize,
              collateral,
              poolBuyPrice,
              poolSellPrice,
              poolInvestorsRaw,
              poolInvestors,
              totalSellers,
              totalBuyers,
              totalSold,
              totalBought,
              poolAveragePrice,
              resolutions,
              pricesBeforeTodayFmt,
              offeringDocData,
              issueingFee,
              pnl,
              securityTokenCoingeckoId,
              underlyingAssetId,
              underlyingAsset,
              underlyingAssetCoporate;

            // Get additional security and owner details
            const fetchedSecurityDetails = await fetchSecurityByAddress(
              chainId,
              pool.security,
            );

            const owner =
              fetchedSecurityDetails.length > 0
                ? fetchedSecurityDetails[0].issueManager
                : "";
            const securityCategory =
              fetchedSecurityDetails.length > 0
                ? ethers.utils.parseBytes32String(
                    fetchedSecurityDetails[0]?.productCategory,
                  )
                : "";

            const securityDeatils = pool.tokens.find(
              (tkn) => tkn.address === pool.security,
            );
            const currencyDetails = pool.tokens.find(
              (tkn) => tkn.address === pool.currency,
            );
            const vptDetails = pool.tokens.find(
              (tkn) => tkn.address === pool.address,
            );

            // Format primary issue pools
            if (pool.poolType === PoolType.primaryPool) {
              const buyOrders = pool.primarySubscriptions.filter(
                (ord) => ord.assetIn.address === pool.currency,
              );
              const sellOrders = pool.primarySubscriptions.filter(
                (ord) => ord.assetIn.address === pool.security,
              );

              const buyOrdersFmt = buyOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.subscription);

                  if (!acc.investors.includes(curr.investor?.id)) {
                    acc.investors.push(curr.investor?.id);
                  }

                  if (Number(acc.date) < Number(curr.executionDate)) {
                    acc.date = Number(curr.executionDate);
                    acc.price = Number(curr.price);
                  }

                  return acc;
                },
                { totalAmount: 0, investors: [], price: 0, date: 0 },
              );

              const sellOrdersFmt = sellOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.subscription);

                  if (
                    !acc.investors.includes(curr.investor?.id?.toLowerCase())
                  ) {
                    acc.investors.push(curr.investor?.id?.toLowerCase());
                  }

                  if (Number(acc.date) < Number(curr.executionDate)) {
                    acc.date = Number(curr.executionDate);
                    acc.price = Number(curr.price);
                  }

                  return acc;
                },
                { totalAmount: 0, investors: [], price: 0, date: 0 },
              );

              prices =
                fetchedSecurityDetails.length > 0
                  ? fetchedSecurityDetails[0]?.primarySubscribers
                      ?.sort(
                        (a, b) => Number(b.timestamp) - Number(a.timestamp),
                      )
                      .map((sub, idx, arr) => {
                        const timeFmt = convertFromBlockTimestamp(
                          sub.timestamp,
                        );
                        const timeFmtArray = timeFmt.split("/");
                        volumeChartData.push({
                          time: Number(sub.timestamp),
                          value: Number(
                            Number(
                              ethers.utils.formatUnits(
                                sub.securitySwapped,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                        });
                        volumeCandleStickData.push({
                          time: Number(sub.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securitySwapped || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                )
                              : Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1]?.securitySwapped || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    sub.securitySwapped,
                                    securityDeatils.decimals,
                                  ),
                                ) >=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.securitySwapped || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securitySwapped || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      sub.securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  ) >=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    sub.securitySwapped,
                                    securityDeatils.decimals,
                                  ),
                                ) <=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.securitySwapped || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securitySwapped || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      sub.securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  ) <=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                          close: Number(
                            Number(
                              ethers.utils.formatUnits(
                                sub.securitySwapped,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                        });
                        pricesRaw.push(
                          Number(
                            (
                              Number(
                                ethers.utils.formatUnits(
                                  sub.cashSwapped,
                                  currencyDetails.decimals,
                                ),
                              ) /
                              Number(
                                ethers.utils.formatUnits(
                                  sub.securitySwapped,
                                  securityDeatils.decimals,
                                ),
                              )
                            ).toFixed(6),
                          ),
                        );
                        priceChartData.push({
                          time: Number(sub.timestamp),
                          value: Number(
                            (
                              Number(
                                ethers.utils.formatUnits(
                                  sub.cashSwapped,
                                  currencyDetails.decimals,
                                ),
                              ) /
                              Number(
                                ethers.utils.formatUnits(
                                  sub.securitySwapped,
                                  securityDeatils.decimals,
                                ),
                              )
                            ).toFixed(6),
                          ),
                        });
                        priceCandleStickData.push({
                          time: Number(sub.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  (
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.cashSwapped || 0.0,
                                        currencyDetails.decimals,
                                      ),
                                    ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securitySwapped || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    )
                                  ).toFixed(6),
                                )
                              : Number(
                                  (
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].cashSwapped,
                                        currencyDetails.decimals,
                                      ),
                                    ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    )
                                  ).toFixed(6),
                                ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    sub.cashSwapped,
                                    currencyDetails.decimals,
                                  ),
                                ) /
                                  Number(
                                    ethers.utils.formatUnits(
                                      sub.securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  ) >=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.cashSwapped || 0.0,
                                    currencyDetails.decimals,
                                  ),
                                ) /
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securitySwapped || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                                : Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.cashSwapped || 0.0,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.securitySwapped || 0.0,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      sub.cashSwapped,
                                      currencyDetails.decimals,
                                    ),
                                  ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ) >=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].cashSwapped,
                                      currencyDetails.decimals,
                                    ),
                                  ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    )
                                ? Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                                : Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  ),
                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    sub.cashSwapped,
                                    currencyDetails.decimals,
                                  ),
                                ) /
                                  Number(
                                    ethers.utils.formatUnits(
                                      sub.securitySwapped,
                                      securityDeatils.decimals,
                                    ),
                                  ) <=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.cashSwapped || 0.0,
                                    currencyDetails.decimals,
                                  ),
                                ) /
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securitySwapped || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                                : Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.cashSwapped || 0.0,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.securitySwapped || 0.0,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      sub.cashSwapped,
                                      currencyDetails.decimals,
                                    ),
                                  ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        sub.securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    ) <=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].cashSwapped,
                                      currencyDetails.decimals,
                                    ),
                                  ) /
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securitySwapped,
                                        securityDeatils.decimals,
                                      ),
                                    )
                                ? Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          sub.securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  )
                                : Number(
                                    (
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].cashSwapped,
                                          currencyDetails.decimals,
                                        ),
                                      ) /
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].securitySwapped,
                                          securityDeatils.decimals,
                                        ),
                                      )
                                    ).toFixed(6),
                                  ),
                          close: Number(
                            (
                              Number(
                                ethers.utils.formatUnits(
                                  sub.cashSwapped,
                                  currencyDetails.decimals,
                                ),
                              ) /
                              Number(
                                ethers.utils.formatUnits(
                                  sub.securitySwapped,
                                  securityDeatils.decimals,
                                ),
                              )
                            ).toFixed(6),
                          ),
                        });
                        return [
                          Date.UTC(
                            Number(timeFmtArray[2]),
                            Number(timeFmtArray[0]) - 1,
                            Number(timeFmtArray[1]),
                          ),
                          Number(
                            (
                              Number(
                                ethers.utils.formatUnits(
                                  sub.cashSwapped,
                                  currencyDetails.decimals,
                                ),
                              ) /
                              Number(
                                ethers.utils.formatUnits(
                                  sub.securitySwapped,
                                  securityDeatils.decimals,
                                ),
                              )
                            ).toFixed(6),
                          ),
                        ];
                      })
                  : [];
              currentPrice = prices.length > 0 ? Number(prices[0][1]) : "0.00";
              minOrderSize = pool.minimumOrderSize;
              poolInvestorsRaw = buyOrdersFmt.investors;
              poolInvestors = buyOrdersFmt.investors.length;
              totalBuyers = buyOrdersFmt.investors.length;
              totalSellers = sellOrdersFmt.investors.length;
              totalBought = buyOrdersFmt.totalAmount;
              poolAveragePrice = (
                buyOrdersFmt.totalAmount / buyOrders.length
              ).toFixed(6);
              totalSold = sellOrdersFmt.totalAmount;
              poolBuyPrice = Number(buyOrdersFmt?.price).toFixed(4);
              poolSellPrice = sellOrdersFmt?.price?.toFixed(4);
              const yesterdayDateRaw = getYesterdayTimeFromNow();
              const pricesBeforeToday = prices.filter(
                (pr) => new Date().getTime() > Number(pr[0]),
              );
              offeringDocData = await readIpfsDocumentFromHash(
                pool?.offeringDocs,
              );
              pricesBeforeTodayFmt = pricesBeforeToday.reduce(
                (acc, curr) => {
                  if (
                    Number(curr[0]) >= Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.yesterdayPrice = Number(curr[1]);
                  }

                  if (
                    Number(curr[0]) < Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.priceBeforeToday = Number(curr[1]);
                  }
                  return acc;
                },
                { yesterdayPrice: 0, priceBeforeToday: 0 },
              );

              if (
                includeResolutions &&
                investorAddress?.length > 0 &&
                fetchedSecurityDetails?.length > 0
              ) {
                const isPrimarySubscriber =
                  fetchedSecurityDetails[0]?.primarySubscribers?.find(
                    (data) =>
                      data?.investor?.id.toLowerCase() ===
                      investorAddress.toLowerCase(),
                  );
                if (isPrimarySubscriber && web3) {
                  const resolutionPromises =
                    fetchedSecurityDetails[0]?.resolutions?.map(
                      async (resolution) => {
                        const securityTokenContract = new web3.eth.Contract(
                          SECURITYABI,
                          resolution.security.id,
                        );
                        const securityTokenSymbol = securityDeatils.symbol;
                        const voteCounts = await securityTokenContract.methods
                          .countVotes(resolution.recordDate)
                          .call()
                          .then((res) => res);
                        let votingDisabled = false;
                        const currentDate = new Date();
                        const fileData = await readIpfsDocumentFromHash(
                          resolution.resolution,
                        );
                        if (
                          new Date(resolution.recordDate * 1000) < currentDate
                        )
                          votingDisabled = true;
                        return {
                          id: resolution.security.id + resolution.recordDate,
                          securityAddress: resolution.security.id,
                          securityTokenSymbol: securityTokenSymbol,
                          offeringDocs: resolution.resolution,
                          fileData,
                          dateRaw: resolution.recordDate,
                          date: convertFromBlockTimestamp(
                            resolution.recordDate,
                          ),
                          isVotable: resolution.voting ? "Yes" : "No",
                          poolType: pool.poolType,
                          votingDisabled,
                          totalVotes: voteCounts?.total,
                          totalPositives: voteCounts?.yes,
                          resolutionType: votingDisabled
                            ? "closed"
                            : resolution.voting
                              ? "upcoming-votable"
                              : "upcoming",
                        };
                      },
                    );
                  resolutions = await Promise.all(resolutionPromises).then(
                    (res) => res,
                  );
                }
              }
            }

            //format secondary issue pools
            if (pool.poolType === PoolType.secondaryPool) {
              const buyOrders = pool.orders.filter(
                (ord) => ord.tokenIn.address === pool.currency,
              );
              const sellOrders = pool.orders.filter(
                (ord) => ord.tokenIn.address === pool.security,
              );
              const buyOrdersFmt = buyOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.amountOffered);

                  if (!acc.investors.includes(curr.creator?.toLowerCase())) {
                    acc.investors.push(curr.creator?.toLowerCase());
                  }
                  return acc;
                },
                { totalAmount: 0, investors: [] },
              );
              const sellOrdersFmt = sellOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.amountOffered);

                  if (!acc.investors.includes(curr.creator)) {
                    acc.investors.push(curr.creator);
                  }
                  return acc;
                },
                { totalAmount: 0, investors: [] },
              );

              prices =
                fetchedSecurityDetails.length > 0
                  ? fetchedSecurityDetails[0].secondaryInvestors
                      ?.sort(
                        (a, b) => Number(b.timestamp) - Number(a.timestamp),
                      )
                      .map((inv, idx, arr) => {
                        const timeFmt = convertFromBlockTimestamp(
                          inv.timestamp,
                        );
                        const timeFmtArray = timeFmt.split("/");
                        volumeChartData.push({
                          time: Number(inv.timestamp),
                          value: Number(
                            Number(
                              ethers.utils.formatUnits(
                                inv.amount,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                        });
                        volumeCandleStickData.push({
                          time: Number(inv.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.amount || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                )
                              : Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].amount,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    inv.amount,
                                    securityDeatils.decimals,
                                  ),
                                ) >=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.amount || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        inv.amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.amount || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      inv.amount,
                                      securityDeatils.decimals,
                                    ),
                                  ) >=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].amount,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        inv.amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    inv.amount,
                                    securityDeatils.decimals,
                                  ),
                                ) <=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.amount || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        inv.amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.amount || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      inv.amount,
                                      securityDeatils.decimals,
                                    ),
                                  ) <=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].amount,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        inv.amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].amount,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                          close: Number(
                            Number(
                              ethers.utils.formatUnits(
                                inv.amount,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                        });
                        pricesRaw.push(
                          Number(
                            Number(ethers.utils.formatEther(inv.price)).toFixed(
                              6,
                            ),
                          ),
                        );
                        priceChartData.push({
                          time: Number(inv.timestamp),
                          value: Number(
                            Number(ethers.utils.formatEther(inv.price)).toFixed(
                              6,
                            ),
                          ),
                        });
                        priceCandleStickData.push({
                          time: Number(inv.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  Number(
                                    arr[idx + 1]?.price
                                      ? ethers.utils.formatEther(
                                          arr[idx + 1]?.price,
                                        )
                                      : 0.0,
                                  ).toFixed(6),
                                )
                              : Number(
                                  Number(
                                    arr[idx + 1]?.price
                                      ? ethers.utils.formatEther(
                                          arr[idx - 1].price,
                                        )
                                      : 0.0,
                                  ).toFixed(6),
                                ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatEther(inv.price),
                                ).toFixed(6) >=
                                Number(
                                  arr[idx + 1]?.price
                                    ? ethers.utils.formatEther(
                                        arr[idx + 1].price,
                                      )
                                    : 0.0,
                                ).toFixed(6)
                                ? Number(
                                    Number(
                                      ethers.utils.formatEther(inv.price),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      arr[idx + 1]?.price
                                        ? ethers.utils.formatEther(
                                            arr[idx + 1].price,
                                          )
                                        : 0.0,
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatEther(inv.price),
                                  ).toFixed(6) >=
                                  Number(
                                    ethers.utils.formatEther(
                                      arr[idx - 1].price,
                                    ),
                                  ).toFixed(6)
                                ? Number(
                                    Number(
                                      ethers.utils.formatEther(inv.price),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatEther(
                                        arr[idx - 1].price,
                                      ),
                                    ).toFixed(6),
                                  ),
                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatEther(inv.price),
                                ).toFixed(6) <=
                                Number(
                                  arr[idx + 1]?.price
                                    ? ethers.utils.formatEther(
                                        arr[idx + 1].price,
                                      )
                                    : 0.0,
                                ).toFixed(6)
                                ? Number(
                                    Number(
                                      ethers.utils.formatEther(inv.price),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      arr[idx + 1]?.price
                                        ? ethers.utils.formatEther(
                                            arr[idx + 1].price,
                                          )
                                        : 0.0,
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatEther(inv.price),
                                  ).toFixed(6) <=
                                  Number(
                                    ethers.utils.formatEther(
                                      arr[idx - 1].price,
                                    ),
                                  ).toFixed(6)
                                ? Number(
                                    Number(
                                      ethers.utils.formatEther(inv.price),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatEther(
                                        arr[idx - 1].price,
                                      ),
                                    ).toFixed(6),
                                  ),
                          close: Number(
                            Number(ethers.utils.formatEther(inv.price)).toFixed(
                              6,
                            ),
                          ),
                        });
                        return [
                          Date.UTC(
                            Number(timeFmtArray[2]),
                            Number(timeFmtArray[0]) - 1,
                            Number(timeFmtArray[1]),
                          ),
                          Number(
                            Number(ethers.utils.formatEther(inv.price)).toFixed(
                              6,
                            ),
                          ),
                        ];
                      })
                  : [];
              currentPrice = prices.length > 0 ? Number(prices[0][1]) : "0.00";
              poolInvestorsRaw = buyOrdersFmt.investors;
              poolInvestors = buyOrdersFmt.investors.length;
              totalBuyers = buyOrdersFmt.investors.length;
              totalSellers = sellOrdersFmt.investors.length;
              totalBought = buyOrdersFmt.totalAmount;
              poolAveragePrice = (
                buyOrdersFmt.totalAmount / buyOrders.length
              ).toFixed(6);
              totalSold = sellOrdersFmt.totalAmount;
              minOrderSize = ethers.utils.formatEther(pool.minOrderSize);
              const yesterdayDateRaw = getYesterdayTimeFromNow();
              const pricesBeforeToday = prices.filter(
                (pr) => new Date().getTime() > Number(pr[0]),
              );
              pricesBeforeTodayFmt = pricesBeforeToday.reduce(
                (acc, curr) => {
                  if (
                    Number(curr[0]) >= Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.yesterdayPrice = Number(curr[1]);
                  }

                  if (
                    Number(curr[0]) < Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.priceBeforeToday = Number(curr[1]);
                  }
                  return acc;
                },
                { yesterdayPrice: 0, priceBeforeToday: 0 },
              );
              const ipfsHashToUse =
                fetchedSecurityDetails[0]?.restrictions?.find(
                  (rest) =>
                    !rest?.endsWith("0000") &&
                    convertBytes32ToIpfsHash(rest)?.length > 0,
                );

              offeringDocData = ipfsHashToUse
                ? await readIpfsDocumentFromHash(
                    convertBytes32ToIpfsHash(ipfsHashToUse),
                  )
                : null;
              if (
                includeResolutions &&
                investorAddress?.length > 0 &&
                fetchedSecurityDetails?.length > 0
              ) {
                const isSecondaryInvestor =
                  fetchedSecurityDetails[0]?.secondaryInvestors?.find(
                    (data) =>
                      data?.investor?.id?.toLowerCase() ===
                        investorAddress.toLowerCase() ||
                      data?.issuer?.id?.toLowerCase() ===
                        investorAddress.toLowerCase(),
                  );
                if (isSecondaryInvestor && web3) {
                  const resolutionPromises =
                    fetchedSecurityDetails[0]?.resolutions?.map(
                      async (resolution) => {
                        const securityTokenContract = new web3.eth.Contract(
                          SECURITYABI,
                          resolution.security.id,
                        );
                        const securityTokenSymbol = securityDeatils.symbol;
                        const voteCounts = await securityTokenContract.methods
                          .countVotes(resolution.recordDate)
                          .call()
                          .then((res) => res);
                        let votingDisabled = false;
                        const currentDate = new Date();
                        const fileData = await readIpfsDocumentFromHash(
                          resolution.resolution,
                        );
                        if (
                          new Date(resolution.recordDate * 1000) < currentDate
                        )
                          votingDisabled = true;
                        return {
                          id: resolution.security.id + resolution.recordDate,
                          securityAddress: resolution.security.id,
                          securityTokenSymbol: securityTokenSymbol,
                          offeringDocs: resolution.resolution,
                          dateRaw: resolution.recordDate,
                          date: convertFromBlockTimestamp(
                            resolution.recordDate,
                          ),
                          fileData,
                          isVotable: resolution.voting ? "Yes" : "No",
                          poolType: pool.poolType,
                          votingDisabled,
                          totalVotes: voteCounts?.total,
                          totalPositives: voteCounts?.yes,
                          resolutionType: votingDisabled
                            ? "closed"
                            : resolution.voting
                              ? "upcoming-votable"
                              : "upcoming",
                        };
                      },
                    );
                  resolutions = await Promise.all(resolutionPromises).then(
                    (res) => res,
                  );
                }
              }
              if (web3 && chainId) {
                const secondaryManager = new web3.eth.Contract(
                  SECONDARYMANAGERABI,
                  contractAddress[chainId].BalancerSecondaryIssueManager,
                );
                const issueingFeeRaw = await secondaryManager.methods
                  .getIssuingFee(pool.security, pool.currency)
                  .call();
                issueingFee = ethers.utils.formatEther(
                  issueingFeeRaw.toString(),
                );
              }
            }

            // //format margin issue pools
            if (pool.poolType === PoolType.marginPool) {
              const buyOrders = pool.marginOrders.filter(
                (ord) => ord.tokenIn.address === pool.currency,
              );
              const sellOrders = pool.marginOrders.filter(
                (ord) => ord.tokenIn.address === pool.security,
              );
              const buyOrdersFmt = buyOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.amountOffered);

                  if (!acc.investors.includes(curr.creator?.toLowerCase())) {
                    acc.investors.push(curr.creator?.toLowerCase());
                  }
                  return acc;
                },
                { totalAmount: 0, investors: [] },
              );
              const sellOrdersFmt = sellOrders.reduce(
                (acc, curr) => {
                  acc.totalAmount += Number(curr.amountOffered);

                  if (!acc.investors.includes(curr.creator)) {
                    acc.investors.push(curr.creator);
                  }
                  return acc;
                },
                { totalAmount: 0, investors: [] },
              );

              const currencyFiat = currencyDetails?.symbol
                ?.toLowerCase()
                ?.startsWith("vc")
                ? currenciesToFiat[
                    currencyDetails?.symbol
                      ?.toLowerCase()
                      ?.replace("vc", "")
                      ?.toUpperCase()
                  ]
                : currenciesToFiat[
                    currencyDetails?.symbol?.toLowerCase()?.toUpperCase()
                  ];

              const securityTokenCoingecko = coingeckoDefaultIds?.find(
                (tk) =>
                  tk?.symbol?.toLowerCase() ===
                  securityDeatils?.symbol?.toLowerCase(),
              )
                ? coingeckoDefaultIds?.find(
                    (tk) =>
                      tk?.symbol?.toLowerCase() ===
                      securityDeatils?.symbol?.toLowerCase(),
                  )
                : allTokensIdCoinecko?.find(
                    (tk) =>
                      tk?.symbol?.toLowerCase() ===
                      securityDeatils?.symbol?.toLowerCase(),
                  );

              securityTokenCoingeckoId = securityTokenCoingecko?.id;

              const fetchedPrice = pool.cficode
                ? marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()] &&
                  marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()][
                    currencyFiat
                  ]
                  ? marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()][
                      currencyFiat
                    ]
                  : await fetchTokenPriceFromVerified(
                      pool.cficode &&
                        ethers.utils
                          .parseBytes32String(pool.cficode)
                          ?.toLowerCase()
                          ?.startsWith("0x") &&
                        _web3
                        ? _web3.utils.toAscii(
                            ethers.utils.parseBytes32String(pool.cficode),
                          )
                        : pool.cficode
                          ? ethers.utils.parseBytes32String(pool.cficode)
                          : "",
                      tokenGcexAlias[securityDeatils?.symbol?.toUpperCase()]
                        ? tokenGcexAlias[securityDeatils?.symbol?.toUpperCase()]
                        : securityDeatils?.symbol,
                      currencyFiat,
                      "BUY",
                    )
                : // securityTokenCoingecko &&
                  // marginFetchedPrices[securityTokenCoingecko?.id] &&
                  // marginFetchedPrices[securityTokenCoingecko?.id][currencyFiat]
                  //   ? marginFetchedPrices[securityTokenCoingecko?.id][
                  //       currencyFiat
                  //     ]
                  //   : securityTokenCoingecko &&
                  //     !marginFetchedPrices[securityTokenCoingecko?.id]
                  //   ? await fetchTokenPriceCoingecko(
                  //       securityTokenCoingecko?.id,
                  //       currencyFiat
                  //     ).then((prc) => {
                  //       if (Number(prc) > 0) {
                  //         return prc;
                  //       } else {
                  //         return fetchTokenPriceCoinbase(
                  //           tokenGcexAlias[securityDeatils?.symbol?.toUpperCase()]
                  //             ? tokenGcexAlias[
                  //                 securityDeatils?.symbol?.toUpperCase()
                  //               ]
                  //             : securityDeatils?.symbol,
                  //           currencyFiat
                  //         );
                  //       }
                  //     })
                  "0.00";

              if (
                marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()] &&
                Number(fetchedPrice) > 0
              ) {
                marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()][
                  currencyFiat
                ] = fetchedPrice;
              } else if (Number(fetchedPrice) > 0) {
                marginFetchedPrices[securityDeatils?.symbol?.toUpperCase()] = {
                  [currencyFiat]: fetchedPrice,
                };
              }

              const marginCurrentPrice = fetchedPrice
                ? Number(fetchedPrice)
                : "0.00";

              const marginPrices =
                fetchedSecurityDetails.length > 0
                  ? fetchedSecurityDetails[0].marginTraders
                      ?.sort(
                        (a, b) => Number(b.timestamp) - Number(a.timestamp),
                      )
                      .map((trd, idx, arr) => {
                        const timeFmt = convertFromBlockTimestamp(
                          trd.timestamp,
                        );
                        const timeFmtArray = timeFmt.split("/");
                        volumeChartData.push({
                          time: Number(trd.timestamp),
                          value: Number(
                            Number(
                              ethers.utils.formatUnits(
                                trd.securityTraded,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                        });
                        volumeCandleStickData.push({
                          time: Number(trd.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securityTraded || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                )
                              : Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                  ).toFixed(6),
                                ),
                          close: Number(
                            Number(
                              ethers.utils.formatUnits(
                                trd.securityTraded,
                                securityDeatils.decimals,
                              ),
                            ).toFixed(6),
                          ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    trd.securityTraded,
                                    securityDeatils.decimals,
                                  ),
                                ) >=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.securityTraded || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securityTraded || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      trd.securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                  ) >=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    trd.securityTraded,
                                    securityDeatils.decimals,
                                  ),
                                ) <=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.securityTraded || 0.0,
                                    securityDeatils.decimals,
                                  ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securityTraded || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      trd.securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                  ) <=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                    ).toFixed(6),
                                  ),
                        });
                        pricesRaw.push(
                          Number(
                            Number(
                              ethers.utils.formatUnits(
                                trd.cashTraded,
                                currencyDetails.decimals,
                              ) /
                                ethers.utils.formatUnits(
                                  trd.securityTraded,
                                  securityDeatils.decimals,
                                ),
                            ).toFixed(6),
                          ),
                        );
                        priceChartData.push({
                          time: Number(trd.timestamp),
                          value: Number(
                            Number(
                              ethers.utils.formatUnits(
                                trd.cashTraded,
                                currencyDetails.decimals,
                              ) /
                                ethers.utils.formatUnits(
                                  trd.securityTraded,
                                  securityDeatils.decimals,
                                ),
                            ).toFixed(6),
                          ),
                        });
                        priceCandleStickData.push({
                          time: Number(trd.timestamp),
                          open:
                            idx === 0
                              ? Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.cashTraded || 0.0,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        arr[idx + 1]?.securityTraded || 0.0,
                                        securityDeatils.decimals,
                                      ),
                                  ).toFixed(6),
                                )
                              : Number(
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].cashTraded,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                  ).toFixed(6),
                                ),
                          close: Number(
                            Number(
                              ethers.utils.formatUnits(
                                trd.cashTraded,
                                currencyDetails.decimals,
                              ) /
                                ethers.utils.formatUnits(
                                  trd.securityTraded,
                                  securityDeatils.decimals,
                                ),
                            ).toFixed(6),
                          ),
                          high:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    trd.cashTraded,
                                    currencyDetails.decimals,
                                  ) /
                                    ethers.utils.formatUnits(
                                      trd.securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                ) >=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.cashTraded || 0.0,
                                    currencyDetails.decimals,
                                  ) /
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securityTraded || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.cashTraded,
                                        currencyDetails.decimals,
                                      ) /
                                        ethers.utils.formatUnits(
                                          trd.securityTraded,
                                          securityDeatils.decimals,
                                        ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.cashTraded || 0.0,
                                          currencyDetails.decimals,
                                        ) /
                                          ethers.utils.formatUnits(
                                            arr[idx + 1]?.securityTraded || 0.0,
                                            securityDeatils.decimals,
                                          ),
                                      ).toFixed(6),
                                    ),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      trd.cashTraded,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                  ) >=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].cashTraded,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.cashTraded,
                                        currencyDetails.decimals,
                                      ) /
                                        ethers.utils.formatUnits(
                                          trd.securityTraded,
                                          securityDeatils.decimals,
                                        ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].cashTraded,
                                          currencyDetails.decimals,
                                        ) /
                                          ethers.utils.formatUnits(
                                            arr[idx - 1].securityTraded,
                                            securityDeatils.decimals,
                                          ),
                                      ).toFixed(6),
                                    ),
                                  ),

                          low:
                            idx === 0
                              ? Number(
                                  ethers.utils.formatUnits(
                                    trd.cashTraded,
                                    currencyDetails.decimals,
                                  ) /
                                    ethers.utils.formatUnits(
                                      trd.securityTraded,
                                      securityDeatils.decimals,
                                    ),
                                ) <=
                                Number(
                                  ethers.utils.formatUnits(
                                    arr[idx + 1]?.cashTraded || 0.0,
                                    currencyDetails.decimals,
                                  ) /
                                    ethers.utils.formatUnits(
                                      arr[idx + 1]?.securityTraded || 0.0,
                                      securityDeatils.decimals,
                                    ),
                                )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.cashTraded,
                                        currencyDetails.decimals,
                                      ) /
                                        ethers.utils.formatUnits(
                                          trd.securityTraded,
                                          securityDeatils.decimals,
                                        ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx + 1]?.cashTraded || 0.0,
                                          currencyDetails.decimals,
                                        ) /
                                          ethers.utils.formatUnits(
                                            arr[idx + 1]?.securityTraded || 0.0,
                                            securityDeatils.decimals,
                                          ),
                                      ).toFixed(6),
                                    ),
                                  )
                              : Number(
                                    ethers.utils.formatUnits(
                                      trd.cashTraded,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        trd.securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                  ) <=
                                  Number(
                                    ethers.utils.formatUnits(
                                      arr[idx - 1].cashTraded,
                                      currencyDetails.decimals,
                                    ) /
                                      ethers.utils.formatUnits(
                                        arr[idx - 1].securityTraded,
                                        securityDeatils.decimals,
                                      ),
                                  )
                                ? Number(
                                    Number(
                                      ethers.utils.formatUnits(
                                        trd.cashTraded,
                                        currencyDetails.decimals,
                                      ) /
                                        ethers.utils.formatUnits(
                                          trd.securityTraded,
                                          securityDeatils.decimals,
                                        ),
                                    ).toFixed(6),
                                  )
                                : Number(
                                    Number(
                                      Number(
                                        ethers.utils.formatUnits(
                                          arr[idx - 1].cashTraded,
                                          currencyDetails.decimals,
                                        ) /
                                          ethers.utils.formatUnits(
                                            arr[idx - 1].securityTraded,
                                            securityDeatils.decimals,
                                          ),
                                      ).toFixed(6),
                                    ),
                                  ),
                        });
                        return [
                          Date.UTC(
                            Number(timeFmtArray[2]),
                            Number(timeFmtArray[0]) - 1,
                            Number(timeFmtArray[1]),
                          ),
                          Number(
                            Number(
                              Number(
                                ethers.utils.formatUnits(
                                  trd.cashTraded,
                                  currencyDetails.decimals,
                                ),
                              ) /
                                Number(
                                  ethers.utils.formatUnits(
                                    trd.securityTraded,
                                    securityDeatils.decimals,
                                  ),
                                ),
                            ).toFixed(6),
                          ),
                        ];
                      })
                  : [];

              // if (Number(marginCurrentPrice) > 0) {
              //   marginFetchedPrices[securityTokenCoingecko?.id] =
              //     marginFetchedPrices[securityTokenCoingecko?.id]
              //       ? marginFetchedPrices[securityTokenCoingecko?.id]
              //       : {};
              //   marginFetchedPrices[securityTokenCoingecko?.id][currencyFiat] =
              //     marginCurrentPrice;
              // }

              currentPrice =
                Number(marginCurrentPrice) > 0
                  ? marginCurrentPrice
                  : marginPrices[0]
                    ? Number(marginPrices[0][1])
                    : "0.00";
              prices = marginPrices;
              poolInvestorsRaw = buyOrdersFmt.investors;
              poolInvestors = buyOrdersFmt.investors.length;
              totalBuyers = buyOrdersFmt.investors.length;
              totalSellers = sellOrdersFmt.investors.length;
              totalBought = buyOrdersFmt.totalAmount;
              poolAveragePrice = (
                buyOrdersFmt.totalAmount / buyOrders.length
              ).toFixed(6);
              totalSold = sellOrdersFmt.totalAmount;
              const typeInString = ethers.utils.parseBytes32String(
                pool.securityType,
              );
              securityType = typeInString;
              if (typeInString.toLocaleLowerCase().startsWith("0x") && _web3) {
                securityType = _web3.utils.toAscii(typeInString);
              } else {
                securityType = typeInString;
              }
              margin = ethers.utils.formatEther(pool.margin);
              minOrderSize = ethers.utils.formatEther(pool.minOrderSize);
              collateral = ethers.utils.formatEther(pool.collateral);
              const yesterdayDateRaw = getYesterdayTimeFromNow();
              const pricesBeforeToday = prices.filter(
                (pr) => new Date().getTime() > Number(pr[0]),
              );
              pricesBeforeTodayFmt = pricesBeforeToday.reduce(
                (acc, curr) => {
                  if (
                    Number(curr[0]) >= Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.yesterdayPrice = Number(curr[1]);
                  }

                  if (
                    Number(curr[0]) < Number(yesterdayDateRaw) &&
                    acc.priceBeforeToday < Number(curr[1])
                  ) {
                    acc.priceBeforeToday = Number(curr[1]);
                  }
                  return acc;
                },
                { yesterdayPrice: 0, priceBeforeToday: 0 },
              );
              const ipfsHashToUse =
                fetchedSecurityDetails[0]?.restrictions?.find(
                  (rest) =>
                    !rest?.endsWith("0000") &&
                    convertBytes32ToIpfsHash(rest)?.length > 0,
                );

              offeringDocData = ipfsHashToUse
                ? await readIpfsDocumentFromHash(
                    convertBytes32ToIpfsHash(ipfsHashToUse),
                  )
                : null;
              // if (chainId > 0 && investorAddress?.length > 0 && web3) {
              //   if (fetchedPNL.length > 0) {
              //     pnl = fetchedPNL
              //       .map((pnl) => {
              //         if (
              //           pnl?.security?.id?.toLowerCase() ===
              //           pool?.security?.toLowerCase()
              //         ) {
              //           return {
              //             financing: Number(
              //               ethers.utils.formatUnits(
              //                 Number(
              //                   ethers.utils
              //                     .formatEther(pnl.financing.toString())
              //                     ?.toString(),
              //                 )?.toFixed(0),
              //                 currencyDetails.decimals?.toString(),
              //               ),
              //             )?.toFixed(Number(currencyDetails.decimals)),
              //             dividend: Number(
              //               ethers.utils.formatUnits(
              //                 Number(
              //                   ethers.utils
              //                     .formatEther(pnl.dividend.toString())
              //                     ?.toString(),
              //                 )?.toFixed(0),
              //                 currencyDetails.decimals?.toString(),
              //               ),
              //             )?.toFixed(Number(currencyDetails.decimals)),
              //             commission: Number(
              //               ethers.utils.formatUnits(
              //                 Number(
              //                   ethers.utils
              //                     .formatEther(pnl.commission.toString())
              //                     ?.toString(),
              //                 )?.toFixed(0),
              //                 currencyDetails.decimals?.toString(),
              //               ),
              //             )?.toFixed(Number(currencyDetails.decimals)),
              //             date: pnl.settlementTime,
              //             dateFmt: convertFromBlockTimestamp(
              //               Number(pnl.settlementTime),
              //             ),
              //           };
              //         } else {
              //           return null;
              //         }
              //       })
              //       ?.filter((pl) => pl !== null);
              //   } else {
              //     pnl = (
              //       await fetchUserMarginProfitAndLoss(
              //         chainId,
              //         investorAddress,
              //         // eslint-disable-next-line no-loop-func
              //       ).then((res) => {
              //         // console.log("user prf: ", res);
              //         if (res?.length > 0) {
              //           fetchedPNL = res;
              //           return res.map((pnl) => {
              //             if (
              //               pnl?.security?.id?.toLowerCase() ===
              //               pool?.security?.toLowerCase()
              //             ) {
              //               // console.log("user prf crt: ", pnl);
              //               return {
              //                 financing: Number(
              //                   ethers.utils.formatUnits(
              //                     Number(
              //                       ethers.utils
              //                         .formatEther(pnl.financing.toString())
              //                         ?.toString(),
              //                     )?.toFixed(0),
              //                     currencyDetails.decimals?.toString(),
              //                   ),
              //                 )?.toFixed(Number(currencyDetails.decimals)),
              //                 dividend: Number(
              //                   ethers.utils.formatUnits(
              //                     Number(
              //                       ethers.utils
              //                         .formatEther(pnl.dividend.toString())
              //                         ?.toString(),
              //                     )?.toFixed(0),
              //                     currencyDetails.decimals?.toString(),
              //                   ),
              //                 )?.toFixed(Number(currencyDetails.decimals)),
              //                 commission: Number(
              //                   ethers.utils.formatUnits(
              //                     Number(
              //                       ethers.utils
              //                         .formatEther(pnl.commission.toString())
              //                         ?.toString(),
              //                     )?.toFixed(0),
              //                     currencyDetails.decimals?.toString(),
              //                   ),
              //                 )?.toFixed(Number(currencyDetails.decimals)),
              //                 date: pnl.settlementTime,
              //                 dateFmt: convertFromBlockTimestamp(
              //                   Number(pnl.settlementTime),
              //                 ),
              //               };
              //             } else {
              //               return null;
              //             }
              //           });
              //         }
              //       })
              //     )?.filter((pl) => pl !== null);
              //   }
              // }
            }

            const investorSecurityBalance =
              web3 && investorAddress?.length > 0
                ? await new web3.eth.Contract(
                    SECURITYABI,
                    pool.security,
                  ).methods
                    .balanceOf(investorAddress)
                    .call()
                : 0;

            let securityFactory, restrictedCountriesCnt;
            const factoryAddress = contractAddress[chainId]?.SecuritiesFactory;

            if (factoryAddress && web3) {
              securityFactory = new web3.eth.Contract(
                SECURITYFACTORYABI,
                factoryAddress,
              );
              restrictedCountriesCnt = await securityFactory.methods
                .getRestrictedCountries(pool.security)
                .call()
                .then((res) => {
                  return res;
                });

              const securityDetailsCnt =
                pool.security && fetchedSecurityDetails[0]?.issuer?.id
                  ? await securityFactory.methods
                      .getSecurityToken(
                        pool.security,
                        fetchedSecurityDetails[0]?.issuer?.id,
                      )
                      .call()
                  : null;

              isSecurityQualified = securityDetailsCnt
                ? securityDetailsCnt[4]
                : false;
            }
            const restrictedCountries =
              restrictedCountriesCnt?.length > 0 &&
              securityCategory?.toLowerCase() === "amc"
                ? restrictedCountriesCnt.map((country, idx) => {
                    if (idx !== 0 && country) {
                      return country?.endsWith("0000")
                        ? ethers.utils
                            .parseBytes32String(country)
                            ?.toLowerCase()
                        : convertBytes32ToIpfsHash(country);
                    } else {
                      return country;
                    }
                  })
                : fetchedSecurityDetails[0]?.restrictions
                  ? fetchedSecurityDetails[0]?.restrictions?.map((country) => {
                      return country?.endsWith("0000")
                        ? ethers.utils
                            .parseBytes32String(country)
                            ?.toLowerCase()
                        : convertBytes32ToIpfsHash(country);
                    })
                  : [];

            const underlyingAssetIdentifier =
              fetchedSecurityDetails[0]?.restrictions?.find(
                (rest) =>
                  rest?.endsWith("0000") &&
                  ethers.utils.parseBytes32String(rest)?.split(":")?.length ===
                    2,
              );

            if (underlyingAssetIdentifier?.length > 0) {
              const underlyingAssetSplit = ethers.utils
                .parseBytes32String(underlyingAssetIdentifier)
                ?.split(":");

              if (underlyingAssetSplit[0] === "GTN") {
                underlyingAsset = await fetchTickerDetails(
                  underlyingAssetSplit[1],
                );
                underlyingAssetCoporate = await getTickerCoporateActions(
                  underlyingAssetSplit[1],
                );
              }

              underlyingAssetId = underlyingAssetSplit[1];
            }
            // Prepare the formatted pool data
            const formattedPool = {
              id: pool.id,
              address: pool.address,
              poolType: pool.poolType,
              priceChartData:
                priceChartData.length > 0
                  ? priceChartData
                      .reduce(
                        (acc, curr) => {
                          if (!acc.timestamps.includes(curr.time)) {
                            acc.timestamps.push(curr.time);
                            acc.data.push(curr);
                          }
                          return acc;
                        },
                        { timestamps: [], data: [] },
                      )
                      .data.reverse()
                  : [{ time: Number(Date.now()) / 1000, value: 0.0 }],
              priceCandleStickData:
                priceCandleStickData.length > 0
                  ? priceCandleStickData
                      .reduce(
                        (acc, curr) => {
                          if (!acc.timestamps.includes(curr.time)) {
                            acc.timestamps.push(curr.time);
                            acc.data.push(curr);
                          }
                          return acc;
                        },
                        { timestamps: [], data: [] },
                      )
                      .data.reverse()
                  : [
                      {
                        time: Number(Date.now()) / 1000,
                        open: 0.0,
                        close: 0.0,
                        high: 0.0,
                        low: 0.0,
                      },
                    ],
              security: pool.security,
              currency: pool.currency,
              vpt: pool.address,
              securityName: securityDeatils.name,
              securitySymbol: securityDeatils.symbol,
              securityBalance: securityDeatils.balance,
              securityDecimals: securityDeatils.decimals,
              securityType,
              securityCategory,
              securityCategoryAlias:
                securityCategoriesAlias[securityCategory.toLowerCase()],
              currencyName: currencyDetails.name,
              currencySymbol: currencyDetails.symbol,
              currencyBalance: currencyDetails.balance,
              currencyDecimals: currencyDetails.decimals,
              currencyFiatSymbol: currencyDetails.symbol,
              vptName: vptDetails.name,
              vptSymbol: vptDetails.symbol,
              vptBalance: vptDetails.balance,
              vptDecimals: vptDetails.decimals,
              prices,
              pricesRawFmt: pricesRaw.map((pr) => Number(pr)),
              highestPrice:
                pricesRaw.length > 0
                  ? Math.max(...pricesRaw.map((pr) => Number(pr)))
                  : 0.0,
              lowestPrice:
                pricesRaw.length > 0
                  ? Math.min(...pricesRaw.map((pr) => Number(pr)))
                  : 0.0,
              currentPrice: underlyingAsset
                ? underlyingAsset?.price
                : currentPrice,
              volumeChartData:
                volumeChartData.length > 0
                  ? volumeChartData
                      .reduce(
                        (acc, curr) => {
                          if (!acc.timestamps.includes(curr.time)) {
                            acc.timestamps.push(curr.time);
                            acc.data.push(curr);
                          }
                          return acc;
                        },
                        { timestamps: [], data: [] },
                      )
                      .data.reverse()
                  : [{ time: Number(Date.now()) / 1000, value: 0.0 }],
              volumeCandleStickData:
                volumeCandleStickData.length > 0
                  ? volumeCandleStickData
                      .reduce(
                        (acc, curr) => {
                          if (!acc.timestamps.includes(curr.time)) {
                            acc.timestamps.push(curr.time);
                            acc.data.push(curr);
                          }
                          return acc;
                        },
                        { timestamps: [], data: [] },
                      )
                      .data.reverse()
                  : [
                      {
                        time: Number(Date.now()) / 1000,
                        open: 0.0,
                        high: 0.0,
                        low: 0.0,
                        close: 0.0,
                      },
                    ],
              tokens: pool.tokens,
              issueingFee,
              swapFee: pool.swapFee,
              tokensList: pool.tokensList,
              margin,
              cfiCode:
                pool.cficode &&
                ethers.utils
                  .parseBytes32String(pool.cficode)
                  ?.toLowerCase()
                  ?.startsWith("0x") &&
                _web3
                  ? _web3.utils.toAscii(
                      ethers.utils.parseBytes32String(pool.cficode),
                    )
                  : pool.cficode
                    ? ethers.utils.parseBytes32String(pool.cficode)
                    : "",
              collateral,
              minOrderSize,
              minimumOrderSize: minOrderSize,
              minPrice: pool.minimumPrice,
              minimumPrice: pool.minimumPrice,
              owner,
              factory: pool.factory,
              orderBook: pool.orderBook,
              offeringDocs: pool.offeringDocs,
              cutoffTime: pool.cutoffTime,
              cutoffTimeFmt: convertFromBlockTimestamp(Number(pool.cutoffTime)),
              createTime: pool.createTime,
              createTimeFmt: convertFromBlockTimestamp(Number(pool.createTime)),
              securityData: fetchedSecurityDetails[0],
              SecurityToFiatPair: currenciesToFiat[currencyDetails.symbol]
                ? `${securityDeatils.symbol}/${
                    currenciesToFiat[currencyDetails.symbol]
                  }`
                : `${securityDeatils.symbol}/${currencyDetails.symbol}`,
              primarySubscriptions: pool.primarySubscriptions,
              secondaryPreTrades: pool.secondaryPreTrades,
              secondaryTrades: pool.secondaryTrades,
              securityOffered: pool.securityOffered,
              swapEnabled: pool.swapEnabled,
              orders: pool.orders,
              marginOrders: pool.marginOrders,
              totalShares: pool.totalShares,
              totalSwapFee: pool.totalSwapFee,
              totalSwapVolume: pool.totalSwapVolume,
              restrictedCountries,
              poolInvestorsRaw,
              poolInvestors,
              poolBuyPrice,
              poolSellPrice,
              totalBought,
              totalBuyers,
              totalSold,
              tvl:
                Number(totalBought) + Number(totalSold) > 0
                  ? formatNumberWithUnits(
                      Number(totalBought) + Number(totalSold),
                    )
                  : "0",
              totalSellers,
              offeringDocData,
              logo:
                offeringDocData?.Business?.Logo ||
                offeringDocData?.Business?.Icon,
              totalInvestorBalance:
                Number(investorSecurityBalance) > 0
                  ? ethers.utils.formatUnits(
                      investorSecurityBalance,
                      securityDeatils.decimals,
                    )
                  : 0,
              totalInvestorSecurityValue:
                Number(investorSecurityBalance) > 0
                  ? ethers.utils.formatUnits(
                      investorSecurityBalance,
                      securityDeatils.decimals,
                    ) * currentPrice
                  : 0,
              totalInvestorSecurityValueYesterday:
                Number(investorSecurityBalance) > 0 &&
                pricesBeforeTodayFmt?.yesterdayPrice
                  ? ethers.utils.formatUnits(
                      investorSecurityBalance,
                      securityDeatils.decimals,
                    ) * pricesBeforeTodayFmt?.yesterdayPrice
                  : 0,
              totalInvestorSecurityValueBeforeToday:
                Number(investorSecurityBalance) > 0 &&
                pricesBeforeTodayFmt?.priceBeforeToday
                  ? ethers.utils.formatUnits(
                      investorSecurityBalance,
                      securityDeatils.decimals,
                    ) * Number(pricesBeforeTodayFmt?.priceBeforeToday)
                  : 0,
              totalInvestorSecurityValueDefault:
                Number(investorSecurityBalance) > 0 && prices.length > 0
                  ? ethers.utils.formatUnits(
                      investorSecurityBalance,
                      securityDeatils.decimals,
                    ) * Number(prices[prices.length - 1][1])
                  : 0,
              poolAveragePrice,
              resolutions,
              name: securityDeatils.name,
              label: securityCategory,
              description: securityDeatils.symbol,
              favourite: false,
              categories: capitalizeFirstLetter(securityCategory),
              region: fetchedSecurityDetails[0]?.ccountry,
              pricesBeforeTodayFmt,
              pnl,
              securityTokenCoingeckoId,
              isCustodyTied: fetchedSecurityDetails[0]?.restrictions?.find(
                (rest) =>
                  rest?.endsWith("0000") &&
                  ethers.utils
                    .parseBytes32String(rest)
                    ?.toLowerCase()
                    ?.startsWith("custody"),
              )
                ? true
                : false,

              custodyProvider: fetchedSecurityDetails[0]?.restrictions?.find(
                (rest) =>
                  rest?.endsWith("0000") &&
                  ethers.utils
                    .parseBytes32String(rest)
                    ?.toLowerCase()
                    ?.startsWith("custody") &&
                  ethers.utils.parseBytes32String(rest)?.split(":PROV:")
                    ?.length === 2,
              )
                ? ethers.utils
                    .parseBytes32String(
                      fetchedSecurityDetails[0]?.restrictions?.find(
                        (rest) =>
                          rest?.endsWith("0000") &&
                          ethers.utils
                            .parseBytes32String(rest)
                            ?.toLowerCase()
                            ?.startsWith("custody") &&
                          ethers.utils.parseBytes32String(rest)?.split(":PROV:")
                            ?.length === 2,
                      ),
                    )
                    ?.split(":PROV:")[1]
                : "",

              underlyingAssetId,
              underlyingAsset,
              underlyingAssetCoporate,
              isRestricted:
                investorAddress &&
                userCountry?.length > 0 &&
                restrictedCountries?.includes(
                  countriesDetails
                    ?.find(
                      (cnt) =>
                        cnt?.name?.toLowerCase() ===
                          userCountry?.toLowerCase() ||
                        cnt?.otherNames?.includes(userCountry?.toLowerCase()),
                    )
                    ?.code?.toLowerCase(),
                )
                  ? true
                  : false,
              isNotQualified: false,
            };

            if (shouldPopulate) {
              setData((prev) => [...prev, formattedPool]); //populate pool as soon as it is ready
            }

            // Collect formatted pools for final return
            formattedPoolsArray.push(formattedPool);

            // await new Promise((resolve) => setTimeout(resolve, 1000)); //1 second delay??
          }

          return formattedPoolsArray;
        }

        await formatPoolsSequentially();

        if (shouldPopulate) setLoading(false);
      } catch (err) {
        if (shouldPopulate) {
          setLoading(false);
          console.error("Error while fetching all pools: ", err);
          setError(
            err.message || "Error while fetching all pools. Try again later.",
          );
        }
      }
    };

    fetchAndFormatAllPools();

    //clear shouldpopulate when it's done
    return () => {
      shouldPopulate = false;
    };
  }, [
    verifiedWalletDetails,
    custodyWalletDetails,
    chainId,
    provider,
    web3,
    updateList,
    investorAddress,
    includeResolutions,
    allTokensIdCoinecko,
    userCountry,
    userType,
    poolId,
    newChainId,
  ]);

  return {
    getAllPoolsData: data,
    getAllPoolsLoading: loading,
    getAllPoolsError: error,
    getAllPoolsBlock: fetchedBlock,
  };
};
